// ── IP 관리 — 액션 훅 ─────────────────────────────────────────────────────────
// IP 연결 등록/토글/삭제, 설명/별칭 저장/삭제 로직
import { apiClient } from '../../../api/apiClient';
import { normalizeIp, isLoopbackIp } from './utils';

/**
 * IP 관리 액션 훅
 * @param {object} params
 *   showToast          — 토스트 알림 함수
 *   apiBase            — 범위 주소(/api/admin/ip-scope/...) — 창을 연 출퇴근 화면(본사·회사·통합)
 *   allowShared        — 공용 IP 등록 가능(통합 출퇴근 관리만). 회사 화면은 직원을 꼭 고른다
 *   fetchAll           — 전체 데이터 재로드 콜백
 *   addForm            — IP 추가 폼 현재 값
 *   currentIp          — 현재 관리자 IP
 *   descDraftById      — 설명 드래프트 map { id: text }
 *   aliasDraftByIp     — 별칭 드래프트 map { ip: text }
 *   setBusyKey         — 온라인 사용자 등록 버튼 비지 세터
 *   setAddBusy         — 폼 등록 버튼 비지 세터
 *   setDescBusyId      — 설명 저장 버튼 비지 세터
 *   setAliasBusyIp     — 별칭 저장 버튼 비지 세터
 *   setAliasDeleteBusyIp — 별칭 삭제 버튼 비지 세터
 *   setAddForm         — addForm 전체 교체 세터
 */
export function useIpActions({
  showToast, apiBase, allowShared = false, fetchAll,
  addForm, currentIp,
  descDraftById, aliasDraftByIp,
  setBusyKey, setAddBusy, setDescBusyId, setAliasBusyIp, setAliasDeleteBusyIp,
  setAddForm,
}) {
  // ── 공통: IP 연결 생성 (내부 헬퍼) ────────────────────────────────────
  async function createConnection(payload, successMessage) {
    const result = await apiClient.post(`${apiBase}/ips`, payload);
    if (!result.ok) {
      showToast(result.data?.message || '등록 실패', 'error');
      return false;
    }
    showToast(successMessage, 'success');
    await fetchAll();
    return true;
  }

  // ── 현재 IP 폼에 채우기 ────────────────────────────────────────────────
  function fillCurrentIp() {
    const ip = normalizeIp(currentIp);
    if (!ip || ip === '-' || isLoopbackIp(ip)) {
      showToast('현재 입력 가능한 외부 IP를 찾지 못했습니다.', 'error');
      return;
    }
    setAddForm((prev) => ({ ...prev, ip_address: ip }));
  }

  // ── 온라인 사용자 → 빠른 등록 (직원 전용 or 공용) ─────────────────────
  async function addFromOnlineUser(user, asPublic) {
    const ip = normalizeIp(user.ip_address);
    if (!ip) return;

    const key = `${asPublic ? 'public' : Number(user.user_id)}|${ip}`;
    setBusyKey(key);
    await createConnection(
      {
        ip_address: ip,
        user_id:    asPublic ? null : Number(user.user_id),
        description: asPublic
          ? '전체 직원 공용 출퇴근 허용 IP'
          : `${user.name} 계정 전용 출퇴근 허용 IP`,
      },
      asPublic ? '공용 IP로 등록했습니다.' : '직원 전용 IP로 등록했습니다.'
    );
    setBusyKey('');
  }

  // ── 폼 직접 등록 ─────────────────────────────────────────────────────
  async function submitAddForm() {
    const ip = normalizeIp(addForm.ip_address);
    if (!ip) {
      showToast('IP 주소를 입력해주세요.', 'error');
      return;
    }
    if (!allowShared && !addForm.user_id) {
      showToast('연결할 직원을 골라 주세요.', 'error');
      return;
    }

    setAddBusy(true);
    const ok = await createConnection(
      {
        ip_address:  ip,
        description: String(addForm.description || '').trim() || null,
        label:       String(addForm.label || '').trim() || null,
        user_id:     addForm.user_id ? Number(addForm.user_id) : null,
      },
      'IP 연결을 등록했습니다.'
    );
    setAddBusy(false);

    if (ok) {
      // IP 주소·설명·별칭만 초기화, user_id는 유지
      setAddForm((prev) => ({ ...prev, ip_address: '', description: '', label: '' }));
    }
  }

  // ── 활성/비활성 토글 ──────────────────────────────────────────────────
  async function toggleConnection(row) {
    const nextActive = row?.is_active === false; // 비활성이면 활성화, 아니면 비활성화
    const result = await apiClient.patch(`${apiBase}/ips/${row.id}`, { is_active: nextActive });
    if (!result.ok) {
      showToast(result.data?.message || '상태 변경 실패', 'error');
      return;
    }
    showToast(nextActive ? '활성화했습니다.' : '비활성화했습니다.', 'success');
    await fetchAll();
  }

  // ── IP 연결 삭제 ─────────────────────────────────────────────────────
  async function removeConnection(row) {
    if (!window.confirm('이 IP 연결을 삭제할까요?')) return;
    const result = await apiClient.del(`${apiBase}/ips/${row.id}`);
    if (!result.ok) {
      showToast(result.data?.message || '삭제 실패', 'error');
      return;
    }
    showToast('삭제했습니다.', 'success');
    await fetchAll();
  }

  // ── IP 주소 바꾸기(2026-10-29) — 지난 출퇴근 기록은 그대로, 새 IP 에 근무지 이름 이어 쓰기 ──────
  //   오늘 그 IP 로 출근만 한 사람이 있으면 서버가 TODAY_OPEN 으로 한 번 묻는다 → 확인하면 다시 보낸다.
  async function changeIp(row, form, confirmToday = false) {
    const ip = normalizeIp(form?.ip);
    if (!ip) {
      showToast('새 IP 주소를 입력해 주세요.', 'error');
      return false;
    }
    const result = await apiClient.patch(`${apiBase}/ips/${row.id}`, {
      ip_address: ip, alias: String(form?.alias ?? '').trim(), confirm_today: confirmToday,
    });
    if (!result.ok) {
      if (result.data?.code === 'TODAY_OPEN' && !confirmToday) {
        if (!window.confirm(`${result.data.message}\n\n그래도 바꿀까요?`)) return false;
        return changeIp(row, form, true);
      }
      showToast(result.data?.message || 'IP 수정 실패', 'error');
      return false;
    }
    const kept = result.data?.alias_kept;
    showToast(kept
      ? `IP를 바꿨어요. 새 IP에는 이미 '${kept}' 이름이 있어 그대로 두었어요.`
      : 'IP를 바꿨어요. 지난 출퇴근 기록은 그대로예요.', 'success');
    await fetchAll();
    return true;
  }

  // ── 설명 저장 ────────────────────────────────────────────────────────
  async function saveDescription(row) {
    const desc = String(descDraftById[row.id] || '').trim();
    setDescBusyId(row.id);
    const result = await apiClient.patch(`${apiBase}/ips/${row.id}`, { description: desc });
    setDescBusyId(null);
    if (!result.ok) {
      showToast(result.data?.message || '설명 저장 실패', 'error');
      return;
    }
    showToast(desc ? '설명을 저장했습니다.' : '설명을 비웠습니다.', 'success');
    await fetchAll();
  }

  // ── 별칭 저장 ────────────────────────────────────────────────────────
  async function saveAlias(ipAddress) {
    const ip = normalizeIp(ipAddress);
    if (!ip) return;
    const aliasText = String(aliasDraftByIp[ip] || '').trim();
    setAliasBusyIp(ip);
    const result = await apiClient.put(`${apiBase}/ip-aliases`, { ip_address: ip, alias: aliasText });
    setAliasBusyIp('');
    if (!result.ok) {
      showToast(result.data?.message || '별칭 저장 실패', 'error');
      return;
    }
    showToast(aliasText ? '별칭을 저장했습니다.' : '별칭을 비웠습니다.', 'success');
    await fetchAll();
  }

  // ── 별칭 삭제 ────────────────────────────────────────────────────────
  async function removeAlias(ipAddress) {
    const ip = normalizeIp(ipAddress);
    if (!ip) return;
    if (!window.confirm(`${ip} 별칭을 삭제할까요?`)) return;
    setAliasDeleteBusyIp(ip);
    const result = await apiClient.del(`${apiBase}/ip-aliases?ip_address=${encodeURIComponent(ip)}`);
    setAliasDeleteBusyIp('');
    if (!result.ok) {
      showToast(result.data?.message || '별칭 삭제 실패', 'error');
      return;
    }
    showToast('별칭을 삭제했습니다.', 'success');
    await fetchAll();
  }

  return {
    fillCurrentIp,
    addFromOnlineUser, submitAddForm,
    toggleConnection, removeConnection, changeIp,
    saveDescription, saveAlias, removeAlias,
  };
}
